/**
 * Encrypted API Service
 * 
 * This service wraps axios to automatically encrypt health data before sending
 * to the server and decrypt data when receiving from the server.
 * 
 * ZERO LIABILITY: The server only sees encrypted blobs - it cannot read your health data.
 */

import axios from 'axios';
import { getVaultKey, isVaultUnlocked } from './vaultCrypto';
import { encryptData, decryptData } from './vaultCrypto';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fields that should be encrypted for each data type
const ENCRYPTED_FIELDS = {
  diary_entries: ['data', 'notes'],
  prescriptions: ['medication_name', 'dosage', 'notes', 'doctor_name'],
  health_reports: ['report_name', 'findings', 'lab_name'],
  medical_history: ['condition', 'diagnosed_date', 'notes', 'doctor_name']
};

/**
 * Encrypt an object's sensitive fields
 * Returns a new object with encrypted_blob containing the encrypted data
 */
export const encryptHealthData = async (data, dataType) => {
  const key = getVaultKey();
  if (!key) {
    console.warn('Vault is locked - sending data unencrypted');
    return data;
  }

  const fieldsToEncrypt = ENCRYPTED_FIELDS[dataType] || [];
  if (fieldsToEncrypt.length === 0) {
    return data;
  }

  // Extract sensitive fields into a separate object
  const sensitiveData = {};
  const sanitizedData = { ...data };

  fieldsToEncrypt.forEach(field => {
    if (data[field] !== undefined) {
      sensitiveData[field] = data[field];
      // Keep a placeholder or remove from main object
      delete sanitizedData[field];
    }
  });

  // If there's sensitive data, encrypt it
  if (Object.keys(sensitiveData).length > 0) {
    try {
      const encryptedBlob = await encryptData(sensitiveData, key);
      sanitizedData.encrypted_blob = encryptedBlob;
      sanitizedData.is_encrypted = true;
    } catch (error) {
      console.error('Encryption failed:', error);
      // Fall back to unencrypted
      return data;
    }
  }

  return sanitizedData;
};

/**
 * Decrypt a record's encrypted_blob and merge back the sensitive fields
 */
export const decryptHealthData = async (record, dataType) => {
  const key = getVaultKey();
  
  // If not encrypted or no key, return as-is
  if (!record.encrypted_blob || !record.is_encrypted || !key) {
    return record;
  }

  try {
    const decryptedData = await decryptData(record.encrypted_blob, key);
    
    // Merge decrypted data back into record
    const result = { ...record, ...decryptedData };
    
    // Remove the encrypted_blob from the result (we have the decrypted data now)
    delete result.encrypted_blob;
    delete result.is_encrypted;
    
    return result;
  } catch (error) {
    console.error('Decryption failed:', error);
    return { ...record, _decryptionFailed: true };
  }
};

/**
 * Decrypt an array of records
 */
export const decryptHealthRecords = async (records, dataType) => {
  if (!Array.isArray(records)) return records;
  
  return Promise.all(
    records.map(record => decryptHealthData(record, dataType))
  );
};

/**
 * Get auth headers
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('sehatSetu_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Encrypted API class
 */
class EncryptedApiService {
  // ==================== DIARY ENTRIES ====================
  
  async createDiaryEntry(entryData, familyMemberId = null) {
    const encryptedData = await encryptHealthData(entryData, 'diary_entries');
    
    const payload = { ...encryptedData };
    if (familyMemberId) {
      payload.family_member_id = familyMemberId;
    }
    
    const response = await axios.post(`${API_URL}/health/diary`, payload, {
      headers: getAuthHeaders()
    });
    
    return response.data;
  }
  
  async getDiaryEntries(familyMemberId = null, entryType = null) {
    let url = `${API_URL}/health/diary`;
    const params = new URLSearchParams();
    
    if (familyMemberId) params.append('family_member_id', familyMemberId);
    if (entryType) params.append('entry_type', entryType);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await axios.get(url, {
      headers: getAuthHeaders()
    });
    
    // Decrypt the records
    const decryptedRecords = await decryptHealthRecords(response.data, 'diary_entries');
    return decryptedRecords;
  }
  
  async updateDiaryEntry(entryId, entryData) {
    const encryptedData = await encryptHealthData(entryData, 'diary_entries');
    
    const response = await axios.put(`${API_URL}/health/diary/${entryId}`, encryptedData, {
      headers: getAuthHeaders()
    });
    
    return response.data;
  }
  
  async deleteDiaryEntry(entryId) {
    const response = await axios.delete(`${API_URL}/health/diary/${entryId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
  
  // ==================== PRESCRIPTIONS ====================
  
  async createPrescription(prescriptionData, familyMemberId = null) {
    const encryptedData = await encryptHealthData(prescriptionData, 'prescriptions');
    
    const payload = { ...encryptedData };
    if (familyMemberId) {
      payload.family_member_id = familyMemberId;
    }
    
    const response = await axios.post(`${API_URL}/health/prescriptions`, payload, {
      headers: getAuthHeaders()
    });
    
    return response.data;
  }
  
  async getPrescriptions(familyMemberId = null) {
    let url = `${API_URL}/health/prescriptions`;
    if (familyMemberId) {
      url += `?family_member_id=${familyMemberId}`;
    }
    
    const response = await axios.get(url, {
      headers: getAuthHeaders()
    });
    
    const decryptedRecords = await decryptHealthRecords(response.data, 'prescriptions');
    return decryptedRecords;
  }
  
  async updatePrescription(entryId, prescriptionData) {
    const encryptedData = await encryptHealthData(prescriptionData, 'prescriptions');
    
    const response = await axios.put(`${API_URL}/health/prescriptions/${entryId}`, encryptedData, {
      headers: getAuthHeaders()
    });
    
    return response.data;
  }
  
  async deletePrescription(entryId) {
    const response = await axios.delete(`${API_URL}/health/prescriptions/${entryId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
  
  // ==================== HEALTH REPORTS ====================
  
  async createReport(reportData, familyMemberId = null) {
    const encryptedData = await encryptHealthData(reportData, 'health_reports');
    
    const payload = { ...encryptedData };
    if (familyMemberId) {
      payload.family_member_id = familyMemberId;
    }
    
    const response = await axios.post(`${API_URL}/health/reports`, payload, {
      headers: getAuthHeaders()
    });
    
    return response.data;
  }
  
  async getReports(familyMemberId = null) {
    let url = `${API_URL}/health/reports`;
    if (familyMemberId) {
      url += `?family_member_id=${familyMemberId}`;
    }
    
    const response = await axios.get(url, {
      headers: getAuthHeaders()
    });
    
    const decryptedRecords = await decryptHealthRecords(response.data, 'health_reports');
    return decryptedRecords;
  }
  
  async deleteReport(entryId) {
    const response = await axios.delete(`${API_URL}/health/reports/${entryId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
  
  // ==================== MEDICAL HISTORY ====================
  
  async createMedicalHistory(historyData) {
    const encryptedData = await encryptHealthData(historyData, 'medical_history');
    
    const response = await axios.post(`${API_URL}/health/medical-history`, encryptedData, {
      headers: getAuthHeaders()
    });
    
    return response.data;
  }
  
  async getMedicalHistory() {
    const response = await axios.get(`${API_URL}/health/medical-history`, {
      headers: getAuthHeaders()
    });
    
    const decryptedRecords = await decryptHealthRecords(response.data, 'medical_history');
    return decryptedRecords;
  }
  
  async updateMedicalHistory(entryId, historyData) {
    const encryptedData = await encryptHealthData(historyData, 'medical_history');
    
    const response = await axios.put(`${API_URL}/health/medical-history/${entryId}`, encryptedData, {
      headers: getAuthHeaders()
    });
    
    return response.data;
  }
  
  async deleteMedicalHistory(entryId) {
    const response = await axios.delete(`${API_URL}/health/medical-history/${entryId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
}

// Export singleton instance
export const encryptedApi = new EncryptedApiService();

// Also export individual functions for flexibility
export default encryptedApi;
